import { useEffect, useState } from 'react';
import whatsappService, {
  WhatsappSessionResponse,
  WhatsappCreateSessionResponse,
} from '@/services/whatsappService';
import { Icon } from '@iconify/react';

const WhatsappConnectTab = ({ onStatusChange }: { onStatusChange?: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<WhatsappSessionResponse | null>(null);
  const [createSession, setCreateSession] = useState<WhatsappCreateSessionResponse | null>(null);

  const loadSession = async () => {
    setIsLoading(true);
    const res = await whatsappService.getSession();
    setSession(res);
    // if session returned a QR and there's no active expiry, initialize one
    try {
      const qrFromSession = res?.data?.qr_code;
      if (qrFromSession) {
        const saved = localStorage.getItem('whatsapp_qr_expires_at');
        const remaining = saved ? Math.ceil((Number(saved) - Date.now()) / 1000) : 0;
        if (!saved || remaining <= 0) {
          const expiresAt = Date.now() + 60_000;
          try {
            localStorage.setItem('whatsapp_qr_expires_at', String(expiresAt));
          } catch (_e) {
            void _e;
          }
          setSecondsLeft(60);
        } else if (remaining > 0) {
          setSecondsLeft(remaining);
        }
      }
    } catch (_e) {
      void _e;
    }
    setIsLoading(false);
    onStatusChange?.();
    return res;
  };

  const createSessionWhatsapp = async () => {
    setIsLoading(true);
    const res = await whatsappService.getWhatsappQRCode();
    setCreateSession(res);
    // if we got a QR (either qrCode or qr_code), initialize countdown immediately and persist expiry
    try {
      const qrVal = (res as any)?.data?.qrCode ?? (res as any)?.data?.qr_code;
      if (qrVal) {
        const expiresAt = Date.now() + 60_000;
        try {
          localStorage.setItem('whatsapp_qr_expires_at', String(expiresAt));
        } catch (_e) {
          void _e;
        }
        setSecondsLeft(60);
      }
    } catch (_e) {
      void _e;
    }
    setIsLoading(false);
    onStatusChange?.();
    return res;
  };

  // countdown state (seconds). When QR is shown, start from 60 and decrement.
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  // on mount: load session first, then restore expiry / create session as needed
  useEffect(() => {
    (async () => {
      const sres = await loadSession();

      // if session is active, clear any stored expiry and stop (no timer)
      if (sres && sres.success && sres.data?.status === 'active') {
        try {
          localStorage.removeItem('whatsapp_qr_expires_at');
        } catch (_e) {
          void _e;
        }
        setSecondsLeft(0);
        return;
      }

      // not active -> check saved expiry to continue timer if exists
      const saved = (() => {
        try {
          return localStorage.getItem('whatsapp_qr_expires_at');
        } catch (_e) {
          void _e;
          return null;
        }
      })();

      let initial = 0;
      if (saved) {
        const expiresAt = Number(saved || 0);
        const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
        if (remaining > 0) initial = remaining;
        else {
          try {
            localStorage.removeItem('whatsapp_qr_expires_at');
          } catch (_e) {
            void _e;
          }
        }
      }

      if (initial > 0) {
        setSecondsLeft(initial);
        return;
      }

      // no saved timer and session not active -> create QR session
      await createSessionWhatsapp();
    })();
  }, []);

  // Poll getSession every 3 seconds while QR is shown/pending to detect when session becomes active
  useEffect(() => {
    const hasQr = !!(createSession?.data?.qrCode ?? session?.data?.qr_code);
    const shouldPoll = hasQr && (session?.data?.status === 'pending' || createSession?.success);
    if (!shouldPoll) return undefined;

    const id = setInterval(async () => {
      try {
        const sres = await whatsappService.getSession();
        setSession(sres);
        // if session becomes active, clear stored expiry and stop polling (effect cleanup will run)
        if (sres?.success && sres.data?.status === 'active') {
          try {
            localStorage.removeItem('whatsapp_qr_expires_at');
          } catch (_e) {
            void _e;
          }
          setSecondsLeft(0);
        }
      } catch (_e) {
        void _e;
      }
    }, 3000);

    return () => clearInterval(id);
  }, [
    session?.data?.status,
    createSession?.success,
    createSession?.data?.qrCode,
    session?.data?.qr_code,
  ]);

  // interval to decrement secondsLeft every second and trigger refresh when hits 0
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;

    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          // time's up: only request a fresh QR when session is not active
          if (session?.data?.status === 'active') {
            return 0;
          }
          createSessionWhatsapp();
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [secondsLeft]);

  useEffect(() => {
    loadSession();
    if (!session || !session.success || session?.data?.status === 'pending') {
      createSessionWhatsapp();
    }
  }, []);

  const handleRefresh = async () => {
    setIsLoading(true);
    const sres = await loadSession();
    try {
      const qrFromSession = sres?.data?.qr_code;
      if (qrFromSession) {
        const expiresAt = Date.now() + 60_000;
        try {
          localStorage.setItem('whatsapp_qr_expires_at', String(expiresAt));
        } catch (_e) {
          void _e;
        }
        setSecondsLeft(60);
      }
    } catch (_e) {
      void _e;
    }
    setIsLoading(false);
    onStatusChange?.();
  };

  // normalize QR value to be string | undefined (avoid null)
  const qr = createSession?.data?.qrCode ?? session?.data?.qr_code ?? undefined;

  return (
    <div className="max-w-8xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8 shadow-2xs">
        {/* Loading State */}
        {isLoading && (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-center">
            <Icon icon="ph:spinner" className="w-7 h-7 text-[#FC480D] animate-spin" />
            <p className="text-xs font-medium text-gray-500">Memeriksa status koneksi WhatsApp...</p>
          </div>
        )}

        {/* QR Code / Pending State */}
        {!isLoading && (createSession?.success || session?.data?.status === 'pending') && (
          <div className="flex flex-col items-center text-center space-y-5">
            <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-200/80 text-[#FC480D] flex items-center justify-center shrink-0">
              <Icon icon="ph:qr-code-bold" className="w-6 h-6" />
            </div>

            <div>
              <h3 className="text-base font-semibold text-[#0E1726]">Scan QR Code WhatsApp</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                Buka WhatsApp di ponsel Anda &gt; Pengaturan &gt; Perangkat Tertaut, lalu arahkan kamera ke kode di bawah.
              </p>
            </div>

            {qr ? (
              <div className="space-y-4">
                <div className="p-3 bg-white border border-gray-200/80 rounded-2xl shadow-xs inline-block">
                  <img
                    src={qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`}
                    alt="Whatsapp QR Code"
                    className="w-56 h-56 object-contain rounded-xl"
                  />
                </div>

                <div className="flex items-center justify-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#FFF5EE] text-[#FC480D] border border-orange-200/80">
                    <Icon icon="ph:clock-bold" className="w-3.5 h-3.5" />
                    {secondsLeft > 0 ? (
                      <span>
                        Diperbarui dalam {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:
                        {String(secondsLeft % 60).padStart(2, '0')}
                      </span>
                    ) : (
                      <span>Memperbarui QR code...</span>
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <div className="p-8 border-2 border-dashed border-gray-200 rounded-2xl text-xs text-gray-400">
                QR Code belum tersedia atau sedang disiapkan.
              </div>
            )}

            <button
              type="button"
              onClick={loadSession}
              className="h-9 px-5 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shadow-2xs active:scale-[0.98]"
            >
              <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5" />
              <span>Refresh Status</span>
            </button>
          </div>
        )}

        {/* Active Connected State */}
        {!isLoading && session?.success && session?.data?.status === 'active' && (
          <div className="flex flex-col items-center text-center space-y-4 py-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center shrink-0">
              <Icon icon="ph:whatsapp-logo-bold" className="w-7 h-7" />
            </div>

            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 mb-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>Koneksi Aktif</span>
              </div>
              <h3 className="text-base font-semibold text-[#0E1726]">WhatsApp Terhubung</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-md">
                Terhubung dengan nomor <strong className="text-[#0E1726]">{session.data?.phone_number || '-'}</strong>.
                Sistem siap mengirimkan notifikasi booking, konfirmasi, dan struk transaksi.
              </p>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-left text-xs text-gray-600 space-y-1.5 w-full max-w-md mt-2">
              <div className="flex items-center gap-2 font-semibold text-[#0E1726]">
                <Icon icon="ph:info-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Informasi Integrasi</span>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Semua pesan diproses otomatis secara terenkripsi. Untuk memutuskan koneksi, silakan logout Sporto API dari menu WhatsApp &gt; Perangkat Tertaut pada ponsel Anda.
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              className="h-9 px-4 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 flex items-center gap-2 transition-all cursor-pointer shadow-2xs mt-2"
            >
              <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5 text-gray-500" />
              <span>Periksa Status</span>
            </button>
          </div>
        )}

        {/* Disconnected State */}
        {!isLoading && session?.data?.status === 'disconnected' && (
          <div className="flex flex-col items-center text-center space-y-4 py-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center shrink-0">
              <Icon icon="ph:link-break-bold" className="w-7 h-7" />
            </div>

            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 mb-2">
                <span>Koneksi Terputus</span>
              </div>
              <h3 className="text-base font-semibold text-[#0E1726]">Sesi WhatsApp Tidak Aktif</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                Koneksi WhatsApp telah diputus atau kedaluwarsa. Klik tombol di bawah untuk menampilkan QR code dan menghubungkan kembali.
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              className="h-9 px-5 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shadow-2xs active:scale-[0.98]"
            >
              <Icon icon="ph:qr-code-bold" className="w-4 h-4" />
              <span>Tampilkan QR / Hubungkan Ulang</span>
            </button>
          </div>
        )}

        {/* Fallback / Error State */}
        {!isLoading && !session && !createSession && (
          <div className="flex flex-col items-center text-center space-y-4 py-8">
            <div className="w-12 h-12 rounded-2xl bg-red-50 border border-red-200 text-red-500 flex items-center justify-center shrink-0">
              <Icon icon="ph:warning-circle-bold" className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#0E1726]">Tidak Dapat Memuat Status</h3>
              <p className="text-xs text-gray-400 mt-1">
                Layanan WhatsApp saat ini tidak merespons. Silakan periksa koneksi atau coba sesaat lagi.
              </p>
            </div>
            <button
              type="button"
              onClick={loadSession}
              className="h-8 px-4 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
            >
              <Icon icon="ph:arrows-clockwise-bold" className="w-3.5 h-3.5 text-gray-500" />
              <span>Coba Lagi</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const WhatsappMessagesTab = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const perPage = 20;
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [selectedMessageContent, setSelectedMessageContent] = useState<string | null>(null);

  const loadMessages = async (p: number = 1, append: boolean = false) => {
    setIsLoading(true);
    try {
      const sres = await whatsappService.getSession();
      const sessionId = sres?.data?.session_id;
      if (!sres || !sres.success || !sessionId) {
        setMessages([]);
        setIsLoading(false);
        return;
      }

      const res = await (whatsappService as any).getSessionMessages(p, perPage);
      if (res && res.success && res.data && Array.isArray(res.data.messages)) {
        const items: any[] = res.data.messages;
        const filtered = items.filter((m) => m.status === 'sent' || m.status === 'error');
        if (append) {
          setMessages((prev) => {
            const start = prev.length;
            const mapped = filtered.map((m, i) => ({ ...m, _no: start + i + 1 }));
            return [...prev, ...mapped];
          });
        } else {
          const mapped = filtered.map((m, i) => ({ ...m, _no: i + 1 }));
          setMessages(mapped);
        }

        const pagination = res.data.pagination;
        if (pagination) {
          setPage(pagination.page || p);
          setHasMore((pagination.page || p) < (pagination.totalPages || 1));
        } else {
          setHasMore(false);
        }
      } else {
        setMessages([]);
        setHasMore(false);
      }
    } catch (_e) {
      setMessages([]);
      setHasMore(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadMessages(1, false);
  }, []);

  const formatDatetime = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const ii = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${MM}/${yyyy} ${hh}:${ii}:${ss}`;
  };

  return (
    <div className="space-y-4">
      {/* Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-[#0E1726]">Riwayat Pengiriman Pesan</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Daftar pesan notifikasi booking, konfirmasi, dan struk pembayaran yang dikirim via WhatsApp.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => loadMessages(1, false)}
            disabled={isLoading}
            className="h-8 px-3.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-[0.98] disabled:opacity-50"
          >
            <Icon icon="ph:arrows-clockwise-bold" className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          {hasMore && (
            <button
              type="button"
              onClick={() => loadMessages(page + 1, true)}
              disabled={isLoading}
              className="h-8 px-3.5 rounded-full bg-[#FFF5EE] text-[#FC480D] hover:bg-orange-100/70 border border-orange-200/80 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs active:scale-[0.98] disabled:opacity-50"
            >
              <Icon icon="ph:caret-down-bold" className="w-3.5 h-3.5" />
              <span>Muat Lebih Banyak</span>
            </button>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-gray-200/80 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                <th className="py-3.5 px-4 text-center w-16">No</th>
                <th className="py-3.5 px-4">Tujuan</th>
                <th className="py-3.5 px-4">Isi Pesan</th>
                <th className="py-3.5 px-4 text-center w-28">Status</th>
                <th className="py-3.5 px-4 text-right w-44">Waktu</th>
                <th className="py-3.5 px-4 text-center w-24">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && messages.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-xs text-gray-400">
                    <Icon icon="ph:spinner" className="w-6 h-6 text-[#FC480D] animate-spin mx-auto mb-2" />
                    <span>Memuat riwayat pesan...</span>
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-xs text-gray-400">
                    <Icon icon="ph:chat-teardrop-slash-bold" className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="font-semibold text-gray-600">Belum ada riwayat pesan</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Pesan yang terkirim akan tercatat secara otomatis di sini.</p>
                  </td>
                </tr>
              ) : (
                messages.map((row, idx) => {
                  const content = row.message_content ?? row.body ?? '';
                  const isSent = row.status === 'sent';

                  return (
                    <tr key={row.id || idx} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3.5 px-4 text-xs font-semibold text-gray-400 text-center">
                        {row._no ?? idx + 1}
                      </td>
                      <td className="py-3.5 px-4 text-xs font-semibold text-[#0E1726]">
                        {row.to_number ?? row.to ?? '-'}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-gray-600 max-w-md truncate">
                        {content}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {isSent ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span>Terkirim</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-600 border border-red-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            <span>Gagal</span>
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-gray-500 text-right font-medium">
                        {formatDatetime(row.sent_at ?? row.created_at ?? row.createdAt)}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedMessageContent(content)}
                          className="h-7 px-3 rounded-full bg-white border border-gray-200 text-gray-700 hover:border-[#FC480D] hover:text-[#FC480D] text-xs font-semibold transition-all cursor-pointer shadow-2xs active:scale-[0.98]"
                        >
                          Lihat
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Detail Pesan */}
      {selectedMessageContent !== null && (
        <div className="fixed inset-0 bg-[#0E1726]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xl max-w-lg w-full p-6 space-y-4 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-50 text-[#FC480D] flex items-center justify-center">
                  <Icon icon="ph:chat-teardrop-text-bold" className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-semibold text-[#0E1726]">Pesan Lengkap</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMessageContent(null)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-xs text-[#0E1726] whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto font-mono">
              {selectedMessageContent || '(Pesan kosong)'}
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setSelectedMessageContent(null)}
                className="h-9 px-5 rounded-full bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-700 transition-all cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WhatsappPage = () => {
  const [activeTab, setActiveTab] = useState<'connect' | 'messages'>('connect');

  const tabs = [
    {
      id: 'connect' as const,
      label: 'Koneksi WhatsApp',
      icon: 'ph:qr-code-bold',
      content: <WhatsappConnectTab onStatusChange={() => {}} />,
    },
    {
      id: 'messages' as const,
      label: 'Riwayat Pesan',
      icon: 'ph:chat-teardrop-text-bold',
      content: <WhatsappMessagesTab />,
    },
  ];

  const activeContent = tabs.find((t) => t.id === activeTab)?.content || tabs[0].content;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-8xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-[#0E1726]">WhatsApp Tools</h1>
        <p className="text-xs md:text-sm text-gray-500 mt-1">
          Kelola koneksi WhatsApp dan pantau riwayat pengiriman notifikasi pemesanan arena secara otomatis.
        </p>
      </div>

      {/* Pill Tab Switcher */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shrink-0 ${
                isActive
                  ? 'bg-white text-[#FC480D] border border-orange-200/80 shadow-2xs font-semibold'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/60'
              }`}
            >
              <Icon icon={tab.icon} className={`w-4 h-4 ${isActive ? 'text-[#FC480D]' : 'text-gray-400'}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="pt-2">{activeContent}</div>
    </div>
  );
};

export default WhatsappPage;
